import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { AxiosInstance } from 'axios'
import {
  ArrayLocationAPIResponse,
  ChecklistCategoryAPIResponse,
  ConstructionStatusAPIResponse,
  ContractTypeAPIResponse,
  SiteRFPDocumentExchangeDetails,
  SiteRFPDocumentExchangeDetailsResponseBody,
} from '@black-bear-energy/black-bear-energy-common'
import { useApiWithIDTokenGranted } from '../../state/modules/common'
import { parseEnum } from './helpers/parseEnum'
import { parseNullableEnum } from './helpers/parseNullableEnum'

/**
 * Fetch the Document-Exchange-specific project details for a site RFP.
 * @param siteRFPId - The ID of the site RFP.
 * @returns A UseQueryResult object containing the Document Exchange details.
 */
export const useSiteRFPDocumentExchangeDetails = (
  siteRFPId: number
): UseQueryResult<SiteRFPDocumentExchangeDetails, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: ['siteRFPDocumentExchangeDetails', api, siteRFPId],
    queryFn: () =>
      fetchSiteRFPDocumentExchangeDetails(api as AxiosInstance, siteRFPId),
    select: transformSiteRFPDocumentExchangeDetails,
    enabled: !!api,
  })
}

async function fetchSiteRFPDocumentExchangeDetails(
  api: AxiosInstance,
  siteRFPId: number
): Promise<SiteRFPDocumentExchangeDetailsResponseBody> {
  const response = await api.get<SiteRFPDocumentExchangeDetailsResponseBody>(
    `/site-rfps/${siteRFPId}/document-exchange-details`
  )
  return response.data
}

export const transformSiteRFPDocumentExchangeDetails = (
  response: SiteRFPDocumentExchangeDetailsResponseBody
): SiteRFPDocumentExchangeDetails => {
  return {
    customerRFPName: response.customerRFPName,
    arrayLocation: parseNullableEnum(
      response.arrayLocation,
      ArrayLocationAPIResponse,
      'array location'
    ),
    utility: response.utility,
    program: response.program,
    availableChecklistCategories: response.availableChecklistCategories.map(
      (category) =>
        parseEnum(category, ChecklistCategoryAPIResponse, 'checklist category')
    ),
    constructionName: response.constructionName,
    constructionStartDate: response.constructionStartDate
      ? new Date(response.constructionStartDate)
      : null,
    commercialOperationDate: response.commercialOperationDate
      ? new Date(response.commercialOperationDate)
      : null,
    commercialOperationDateEstimate: response.commercialOperationDateEstimate
      ? new Date(response.commercialOperationDateEstimate)
      : null,
    constructionStatus: parseNullableEnum(
      response.constructionStatus,
      ConstructionStatusAPIResponse,
      'construction status'
    ),
    contractsName: response.contractsName,
    contractStructure: parseNullableEnum(
      response.contractStructure,
      ContractTypeAPIResponse,
      'contract type'
    ),
    contractExecutionDate: response.contractExecutionDate
      ? new Date(response.contractExecutionDate)
      : null,
    contractExecutionDateEstimate: response.contractExecutionDateEstimate
      ? new Date(response.contractExecutionDateEstimate)
      : null,
  }
}
